import { ReactNode, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type Props = {
  /**
   * Stable per-card id used to persist the open/closed state in localStorage.
   * Keep it specific to the card (e.g. "sign-off.operator-briefing") so a user's
   * preference survives across pages and sessions.
   */
  storageKey: string;
  /** Title rendered in the header (string or custom node). */
  title: ReactNode;
  /** Optional description rendered under the title. */
  description?: ReactNode;
  /** Optional actions rendered on the right of the header (buttons, badges, etc). */
  headerActions?: ReactNode;
  /** Content shown when expanded. */
  children: ReactNode;
  /** Default state when no preference has been stored yet. Defaults to true. */
  defaultOpen?: boolean;
  /** Extra classes applied to the underlying Card. */
  className?: string;
  /** Called whenever the state changes (for integration with external logic). */
  onOpenChange?: (open: boolean) => void;
};

function readInitialOpen(storageKey: string, defaultOpen: boolean): boolean {
  if (typeof window === 'undefined') return defaultOpen;
  try {
    const raw = window.localStorage.getItem(`collapsibleCard:${storageKey}`);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
  } catch {
    // localStorage can throw in private mode / sandboxed contexts — fall through.
  }
  return defaultOpen;
}

export function CollapsibleCard({
  storageKey,
  title,
  description,
  headerActions,
  children,
  defaultOpen = true,
  className,
  onOpenChange,
}: Props) {
  const [open, setOpen] = useState<boolean>(() => readInitialOpen(storageKey, defaultOpen));

  useEffect(() => {
    try {
      window.localStorage.setItem(`collapsibleCard:${storageKey}`, String(open));
    } catch {
      // Swallow storage errors silently; collapse still works for the session.
    }
    onOpenChange?.(open);
  }, [open, storageKey, onOpenChange]);

  const toggle = () => setOpen((v) => !v);

  return (
    <Card className={className}>
      <CardHeader
        className="cursor-pointer select-none"
        onClick={toggle}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2 min-w-0 flex-1">
            <div className="mt-1 text-muted-foreground shrink-0">
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold leading-tight">{title}</div>
              {description && (
                <div className="text-sm text-muted-foreground mt-0.5">{description}</div>
              )}
            </div>
          </div>
          {headerActions && (
            <div
              className="shrink-0"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              {headerActions}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className={cn(!open && 'hidden')}>{children}</CardContent>
    </Card>
  );
}
